import { useLocalSearchParams, useRouter } from "expo-router";
import {
  ArrowRight,
  BarChart3,
  CalendarDays,
  CheckCircle2,
  ChevronDown,
  ClipboardCheck,
  CreditCard,
  FileSignature,
  FileText,
  FolderOpen,
  HeartHandshake,
  Home,
  LayoutDashboard,
  LibraryBig,
  LockKeyhole,
  Mail,
  MessageCircle,
  PackageCheck,
  Phone,
  QrCode,
  Search,
  Settings,
  ShieldCheck,
  Sparkles,
  Users,
} from "../../../components/lucide-icons";
import { ReactNode, useState } from "react";
import {
  Image,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  View,
  useWindowDimensions,
} from "react-native";
import QRCode from "react-native-qrcode-svg";

import { AppShell } from "../../../components/layout";
import { semantic, shadows, spacing } from "../../../theme/tokens";
import { monarchCremationAuthorization } from "../data/monarch-cremation-authorization";
import { monarchReceiptOfRemains } from "../data/monarch-receipt-of-remains";
import {
  demoCaseHref,
  demoStaffHref,
  demoWorkspaceHref,
} from "../utils/demo-navigation";
import {
  WeHonorPlanGroup,
  WeHonorPlanPage,
  getWeHonorPagesByGroup,
  getWeHonorPlanPage,
  weHonorPlanPages,
} from "../data/wehonor-system-plan";

type IconComponent = (props: {
  color: string;
  size: number;
  strokeWidth?: number;
}) => ReactNode;

const monarchBlueLogo = require("../../../../assets/monarch-blue-logo.png");

const groupConfig: Record<
  WeHonorPlanGroup,
  {
    label: string;
    shortLabel: string;
    icon: IconComponent;
    tone: "forest" | "gold" | "cedar" | "mist";
  }
> = {
  family: {
    label: "Family care",
    shortLabel: "Family",
    icon: HeartHandshake,
    tone: "forest",
  },
  staff: {
    label: "Cases",
    shortLabel: "Cases",
    icon: LayoutDashboard,
    tone: "gold",
  },
  paperwork: {
    label: "Paperwork",
    shortLabel: "Forms",
    icon: FileSignature,
    tone: "cedar",
  },
  memorial: {
    label: "Memorials",
    shortLabel: "Memorial",
    icon: MessageCircle,
    tone: "forest",
  },
  office: {
    label: "Office",
    shortLabel: "Office",
    icon: Settings,
    tone: "mist",
  },
};

const familyPathOptions = [
  {
    title: "Immediate need",
    detail:
      "A death has occurred and the family needs Monarch to begin care now.",
    icon: Phone,
  },
  {
    title: "Expected soon",
    detail: "A family wants guidance before arrangements become urgent.",
    icon: CalendarDays,
  },
  {
    title: "Pre-planning",
    detail: "Someone is documenting wishes and family contacts for the future.",
    icon: HeartHandshake,
  },
  {
    title: "Already working with Monarch",
    detail: "Return with a private access link or ask staff to resend it.",
    icon: ShieldCheck,
  },
] as const;

const dashboardMetrics = [
  { label: "Open cases", value: "18", tone: "forest" },
  { label: "Forms ready", value: "12", tone: "gold" },
  { label: "Signatures waiting", value: "7", tone: "cedar" },
  { label: "Balances due", value: "$42.8k", tone: "forest" },
] as const;

const caseRows = [
  {
    family: "Hernandez family",
    lovedOne: "Maria Hernandez",
    status: "Arrangement review",
    owner: "Karen",
    paperwork: "Receipt pending",
    balance: "$3,420 due",
    next: "Confirm urn release and signatures",
  },
  {
    family: "Martin family",
    lovedOne: "Eleanor Martin",
    status: "Forms ready",
    owner: "Bill",
    paperwork: "Cremation auth ready",
    balance: "$0 due",
    next: "Send signature packet",
  },
  {
    family: "Nguyen family",
    lovedOne: "Thomas Nguyen",
    status: "Pre-plan draft",
    owner: "Karen",
    paperwork: "Vital info incomplete",
    balance: "Estimate only",
    next: "Review service choices",
  },
] as const;

const directorWorkQueue = [
  {
    title: "New online arrangement",
    detail:
      "Review Hernandez family cremation selections and missing documents.",
    screenId: "staff-arrangement-review",
  },
  {
    title: "Signature packet waiting",
    detail:
      "Cremation authorization and receipt form are ready for family signature.",
    screenId: "signature-packet",
  },
  {
    title: "Contract balance",
    detail:
      "Statement of Funeral Goods and Services needs final payment status.",
    screenId: "financials",
  },
  {
    title: "Website update",
    detail:
      "Memorial page draft and digital service folder are ready for approval.",
    screenId: "website-memorial-management",
  },
] as const;

const directorFeatureSections = [
  {
    title: "Case Management",
    summary:
      "Everything from the first call through disposition, documents, and staff follow-up.",
    screenIds: [
      "case-list",
      "create-case",
      "case-overview",
      "staff-arrangement-review",
      "call-tracking",
      "case-checklist",
      "vital-statistics-review",
      "document-library",
    ],
  },
  {
    title: "Forms, Signatures, Contracts, and Money",
    summary:
      "Generate paperwork, send signature packets, build statements, and track balances.",
    screenIds: [
      "generated-forms",
      "signature-packet",
      "form-template-library",
      "form-template-editor",
      "contract-builder",
      "financials",
      "reports-exports",
    ],
  },
  {
    title: "Family, Memorial, Guestbook, and Keepsakes",
    summary:
      "Manage family-facing content after staff review, including the website and service folder.",
    screenIds: [
      "website-memorial-management",
      "digital-service-folder-builder",
      "guestbook-moderation",
      "guest-contacts",
      "keepsake-orders",
      "family-follow-up-portal",
    ],
  },
  {
    title: "Office Administration",
    summary:
      "Control branding, staff access, templates, price lists, notifications, and exports.",
    screenIds: [
      "funeral-home-settings",
      "branding-logo-management",
      "catalog-price-list-management",
      "staff-permissions",
      "notification-templates",
      "reports-exports",
    ],
  },
] as const;

const generatedCasePacketForms = [
  { title: "Statement of Funeral Goods and Services", active: false },
  { title: monarchCremationAuthorization.title, active: false },
  { title: monarchReceiptOfRemains.title, active: true },
  { title: "Signature packet cover page", active: false },
  { title: "Payment authorization", active: false },
] as const;

const templateForms = [
  monarchReceiptOfRemains,
  monarchCremationAuthorization,
] as const;

export default function WeHonorSystemWorkspaceScreen({
  screenIdOverride,
}: {
  screenIdOverride?: string;
  routeOverride?: string;
} = {}) {
  const { width } = useWindowDimensions();
  const params = useLocalSearchParams<{ screenId?: string | string[] }>();
  const routeScreenId = Array.isArray(params.screenId)
    ? params.screenId[0]
    : params.screenId;
  const screenId = screenIdOverride ?? routeScreenId;
  const [directorSignedIn, setDirectorSignedIn] = useState(Boolean(screenId));
  const selectedPage = screenId ? getWeHonorPlanPage(screenId) : undefined;
  const activePage =
    selectedPage ??
    getWeHonorPlanPage("staff-dashboard") ??
    weHonorPlanPages[0];
  const isDesktop = width >= 960;
  const isFamilyPublicRoute =
    activePage.id === "monarch-website-start" ||
    activePage.id === "arrangement-type-selection";

  if (!screenId && !directorSignedIn) {
    return (
      <AppShell role="staff" navigation="none" requireStaffSession={false} accessibilityLabel="Director workspace sign in">
        <ScrollView
          contentContainerStyle={styles.directorLoginContent}
          contentInsetAdjustmentBehavior="automatic"
        >
          <StaffLoginScreen
            isDesktop={isDesktop}
            onSignIn={() => setDirectorSignedIn(true)}
          />
        </ScrollView>
      </AppShell>
    );
  }

  if (isFamilyPublicRoute) {
    return (
      <AppShell role="family" navigation="none" accessibilityLabel="Monarch family website">
        <ScrollView
          contentContainerStyle={styles.publicContent}
          contentInsetAdjustmentBehavior="automatic"
        >
          <FamilyPublicHeader isDesktop={isDesktop} />
          <FamilyStartScreen isDesktop={isDesktop} page={activePage} />
        </ScrollView>
      </AppShell>
    );
  }

  return (
    <AppShell role="staff" navigation="none" requireStaffSession={false} accessibilityLabel="WeHonor system workspace">
      <View style={[styles.shell, isDesktop ? null : styles.shellMobile]}>
        {isDesktop ? (
          <WorkspaceNav activePage={activePage} isDesktop={isDesktop} />
        ) : (
          <WorkspaceMobileAccountBar />
        )}
        <ScrollView
          style={styles.scroll}
          contentContainerStyle={[
            styles.content,
            isDesktop ? null : styles.contentMobile,
          ]}
        >
          <WorkspaceHeader isDesktop={isDesktop} />
          <WorkspaceBody page={activePage} isDesktop={isDesktop} />
        </ScrollView>
        {!isDesktop ? (
          <WorkspaceNav activePage={activePage} isDesktop={isDesktop} />
        ) : null}
      </View>
    </AppShell>
  );
}

function WorkspaceNav({
  activePage,
  isDesktop,
}: {
  activePage: WeHonorPlanPage;
  isDesktop: boolean;
}) {
  const router = useRouter();

  return (
    <View style={[styles.nav, isDesktop ? null : styles.navMobile]}>
      {isDesktop ? (
        <Pressable
          accessibilityRole="button"
          accessibilityLabel="Open Monarch staff dashboard"
          onPress={() => router.push(demoStaffHref() as never)}
          style={styles.navLogo}
        >
          <Image
            source={monarchBlueLogo}
            resizeMode="contain"
            style={styles.navLogoImage}
          />
        </Pressable>
      ) : null}

      <View style={[styles.navItems, isDesktop ? null : styles.navItemsMobile]}>
        {(Object.keys(groupConfig) as WeHonorPlanGroup[]).map((group) => {
          const Icon = groupConfig[group].icon;
          const active = activePage.group === group;
          return (
            <Pressable
              key={group}
              accessibilityRole="button"
              accessibilityLabel={groupConfig[group].label}
              accessibilityState={{ selected: active }}
              onPress={() => router.push(firstGroupHref(group) as never)}
              style={[
                styles.navItem,
                isDesktop ? null : styles.navItemMobile,
                active ? styles.navItemActive : null,
              ]}
            >
              <Icon
                color={active ? semantic.accent.soft : semantic.overlay.inverseStrong}
                size={isDesktop ? 21 : 19}
                strokeWidth={1.9}
              />
              <Text
                style={[
                  styles.navItemText,
                  isDesktop ? null : styles.navItemTextMobile,
                  active ? styles.navItemTextActive : null,
                ]}
              >
                {groupConfig[group].shortLabel}
              </Text>
            </Pressable>
          );
        })}
      </View>
    </View>
  );
}

function WorkspaceMobileAccountBar() {
  return (
    <View style={styles.mobileAccountBar}>
      <Pressable
        accessibilityRole="button"
        accessibilityLabel="Switch organization"
        style={styles.mobileOrgButton}
      >
        <View style={styles.mobileLogoMarkFrame}>
          <Text style={styles.mobileLogoMarkText}>M</Text>
        </View>
        <Text style={styles.mobileOrgName}>Monarch</Text>
        <ChevronDown color={semantic.text.secondary} size={16} strokeWidth={2.2} />
      </Pressable>
      <Pressable
        accessibilityRole="button"
        accessibilityLabel="Open account settings"
        style={styles.mobileAccountButton}
      >
        <Text style={styles.mobileAccountText}>KS</Text>
      </Pressable>
    </View>
  );
}

function WorkspaceHeader({ isDesktop }: { isDesktop: boolean }) {
  const router = useRouter();

  if (!isDesktop) {
    return null;
  }

  return (
    <View style={[styles.header, isDesktop ? null : styles.headerMobile]}>
      <View style={styles.headerBrand}>
        <Image
          source={monarchBlueLogo}
          resizeMode="contain"
          style={[
            styles.headerLogo,
            isDesktop ? null : styles.headerLogoMobile,
          ]}
        />
        <Text style={styles.headerSubline}>Monarch operating workspace</Text>
      </View>
      <View style={styles.headerActions}>
        <OutlineButton
          label="Family start"
          icon={Home}
          onPress={() => router.push("/demo/monarch" as never)}
        />
        <SolidButton
          label="Open case"
          icon={FolderOpen}
          onPress={() =>
            router.push(
              demoCaseHref("arr-monarch-maria-hernandez") as never,
            )
          }
        />
      </View>
    </View>
  );
}

function FamilyPublicHeader({ isDesktop }: { isDesktop: boolean }) {
  const router = useRouter();
  return (
    <View
      style={[
        styles.publicHeader,
        isDesktop ? null : styles.publicHeaderMobile,
      ]}
    >
      <Pressable
        accessibilityRole="button"
        accessibilityLabel="Open Monarch website"
        onPress={() => router.push("/demo/monarch" as never)}
        style={styles.publicLogoButton}
      >
        <Image
          source={monarchBlueLogo}
          resizeMode="contain"
          style={[
            styles.publicLogo,
            isDesktop ? null : styles.publicLogoMobile,
          ]}
        />
      </Pressable>
      <View style={styles.publicHeaderActions}>
        <OutlineButton
          label="Back to website"
          icon={Home}
          onPress={() => router.push("/demo/monarch" as never)}
        />
        <SolidButton
          label="Call Monarch"
          icon={Phone}
          onPress={() => undefined}
        />
      </View>
    </View>
  );
}

function WorkspaceBody({
  page,
  isDesktop,
}: {
  page: WeHonorPlanPage;
  isDesktop: boolean;
}) {
  if (page.id === "staff-login")
    return <StaffLoginScreen isDesktop={isDesktop} />;
  if (
    page.id === "monarch-website-start" ||
    page.id === "arrangement-type-selection"
  ) {
    return <FamilyStartScreen isDesktop={isDesktop} page={page} />;
  }
  if (page.id === "staff-dashboard")
    return <StaffDashboardScreen isDesktop={isDesktop} />;
  if (page.id === "generated-forms")
    return <GeneratedFormsScreen isDesktop={isDesktop} />;
  if (
    page.id === "form-template-editor" ||
    page.id === "form-template-library"
  ) {
    return <FormTemplateScreen isDesktop={isDesktop} page={page} />;
  }
  return <OperationalScreen page={page} isDesktop={isDesktop} />;
}

function FamilyStartScreen({
  page,
  isDesktop,
}: {
  page: WeHonorPlanPage;
  isDesktop: boolean;
}) {
  const router = useRouter();
  return (
    <View style={styles.pageStack}>
      <View
        style={[styles.familyHero, isDesktop ? styles.familyHeroDesktop : null]}
      >
        <View
          style={[
            styles.familyHeroCopy,
            isDesktop ? null : styles.familyHeroCopyMobile,
          ]}
        >
          <Badge label="Family arrangement" tone="forest" />
          <Text
            style={[
              styles.displayTitle,
              isDesktop ? null : styles.displayTitleMobile,
            ]}
          >
            How can Monarch help you today?
          </Text>
          <Text style={styles.displayBody}>
            Choose the path that matches the family&apos;s situation. Monarch
            can receive the details online, call the family, and keep every next
            action tied to the same case.
          </Text>
          {isDesktop ? (
            <View style={styles.actionRow}>
              <SolidButton
                label="Continue"
                icon={ArrowRight}
                onPress={() =>
                  router.push("/demo/monarch/arrangements/studio" as never)
                }
              />
              <OutlineButton
                label="Call Monarch"
                icon={Phone}
                onPress={() => undefined}
              />
            </View>
          ) : null}
        </View>
        <View style={styles.contactCard}>
          <Text style={styles.contactCardTitle}>Monarch Funeral Home</Text>
          <InfoRow icon={Phone} text="(954) 972-7565" />
          <InfoRow icon={Home} text="2950 N State Rd 7, Margate, FL 33063" />
          <InfoRow
            icon={ShieldCheck}
            text="Private family access and Monarch review"
          />
        </View>
      </View>

      <View style={styles.optionGrid}>
        {familyPathOptions.map((option) => (
          <ChoiceCard
            key={option.title}
            icon={option.icon}
            title={option.title}
            body={option.detail}
            onPress={() =>
              router.push("/demo/monarch/arrangements/studio" as never)
            }
          />
        ))}
      </View>

      <View
        style={[styles.workBand, isDesktop ? styles.workBandDesktop : null]}
      >
        <View style={styles.bandCopy}>
          <Text style={styles.sectionEyebrow}>
            What happens after the family starts
          </Text>
          <Text style={styles.sectionTitle}>
            Their answers flow into one reviewed Monarch case.
          </Text>
          <Text style={styles.sectionBody}>{page.purpose}</Text>
        </View>
        <View style={styles.checkList}>
          {[
            "Family contact is saved",
            "Loved one details begin the case",
            "Paperwork is prepared for staff",
            "Missing information stays visible",
          ].map((item) => (
            <CheckRow key={item} label={item} />
          ))}
        </View>
      </View>
    </View>
  );
}

function StaffLoginScreen({
  isDesktop,
  onSignIn,
}: {
  isDesktop: boolean;
  onSignIn?: () => void;
}) {
  const router = useRouter();
  const handleSignIn = () => {
    if (onSignIn) {
      onSignIn();
      return;
    }

    router.push(screenHref("staff-dashboard") as never);
  };

  return (
    <View
      style={[styles.loginWrap, isDesktop ? styles.loginWrapDesktop : null]}
    >
      <View style={styles.loginCopy}>
        <Badge label="Funeral home admin" tone="gold" />
        <Text
          style={[
            styles.displayTitle,
            isDesktop ? null : styles.displayTitleMobile,
          ]}
        >
          Sign in to the admin dashboard.
        </Text>
        <Text style={styles.displayBody}>
          This is the staff-side entrance for running the funeral home: cases,
          calls, paperwork, signatures, contracts, financials, memorial pages,
          guestbook activity, and follow-up.
        </Text>
        <View style={styles.securityList}>
          <CheckRow label="Role-based access for admins and office staff" />
          <CheckRow label="Private case, paperwork, and payment information" />
          <CheckRow label="One dashboard after sign-in" />
        </View>
      </View>

      <View style={styles.loginPanel}>
        <View style={styles.loginIcon}>
          <LockKeyhole color={semantic.text.inverse} size={24} strokeWidth={1.9} />
        </View>
        <Text style={styles.loginTitle}>Monarch admin sign-in</Text>
        <Text style={styles.loginText}>Use your work email to continue.</Text>
        <Field
          label="Email"
          placeholder="karen@monarchfuneralhome.com"
          icon={Mail}
        />
        <Field
          label="Password"
          placeholder="Password"
          icon={LockKeyhole}
          secureTextEntry
        />
        <SolidButton
          label="Sign in"
          icon={ArrowRight}
          onPress={handleSignIn}
          fullWidth
        />
        <OutlineButton
          label="Send secured sign-in link"
          icon={Mail}
          onPress={handleSignIn}
          fullWidth
        />
      </View>
    </View>
  );
}

function StaffDashboardScreen({ isDesktop }: { isDesktop: boolean }) {
  const router = useRouter();
  return (
    <View style={styles.pageStack}>
      <View
        style={[
          styles.dashboardHero,
          isDesktop ? styles.dashboardHeroDesktop : null,
        ]}
      >
        <View style={styles.dashboardHeroCopy}>
          {isDesktop ? <Badge label="Admin dashboard" tone="gold" /> : null}
          <Text
            style={[
              styles.displayTitle,
              isDesktop ? null : styles.displayTitleMobile,
            ]}
          >
            Run the funeral home from one daily workspace.
          </Text>
          <Text style={styles.displayBody}>
            After login, staff see the operational picture first: active cases,
            calls, missing paperwork, forms, signatures, contracts, balances,
            memorial content, and follow-up.
          </Text>
          <View
            style={[
              styles.actionRow,
              isDesktop ? null : styles.actionRowMobile,
            ]}
          >
            <SolidButton
              label="Create case"
              icon={ClipboardCheck}
              onPress={() => router.push(screenHref("create-case") as never)}
              fullWidth={!isDesktop}
            />
            {isDesktop ? (
              <OutlineButton
                label="Open case list"
                icon={LayoutDashboard}
                onPress={() => router.push(screenHref("case-list") as never)}
              />
            ) : null}
          </View>
        </View>
        <View style={styles.searchBox}>
          <Search color={semantic.action.primary} size={18} strokeWidth={2} />
          <Text style={styles.searchText}>Search cases, forms, families</Text>
        </View>
      </View>

      <View style={styles.metricGrid}>
        {dashboardMetrics.map((metric) => (
          <MetricCard
            key={metric.label}
            value={metric.value}
            label={metric.label}
            tone={metric.tone}
          />
        ))}
      </View>

      <View style={[styles.dashboardGrid, isDesktop ? null : styles.stack]}>
        <View style={styles.panelLarge}>
          <View style={styles.panelHeader}>
            <LayoutDashboard
              color={semantic.action.primary}
              size={20}
              strokeWidth={1.9}
            />
            <Text style={styles.panelTitle}>Active case board</Text>
          </View>
          <View style={styles.caseTable}>
            {caseRows.map((row) => (
              <Pressable
                key={row.family}
                accessibilityRole="button"
                onPress={() =>
                  router.push(
                    demoCaseHref("arr-monarch-maria-hernandez") as never,
                  )
                }
                style={styles.caseRow}
              >
                <View style={styles.caseFamily}>
                  <Text style={styles.caseName}>{row.lovedOne}</Text>
                  <Text style={styles.caseMeta}>{row.family}</Text>
                </View>
                <Text style={styles.caseOwner}>{row.status}</Text>
                <Text style={styles.caseOwner}>{row.owner}</Text>
                <Text style={styles.caseNext}>{row.paperwork}</Text>
                <Text style={styles.caseOwner}>{row.balance}</Text>
                <Text style={styles.caseNext}>{row.next}</Text>
              </Pressable>
            ))}
          </View>
        </View>

        <View style={styles.panelSide}>
          <View style={styles.panelHeader}>
            <Sparkles color={semantic.action.primary} size={20} strokeWidth={1.9} />
            <Text style={styles.panelTitle}>Today&apos;s work queue</Text>
          </View>
          {directorWorkQueue.map((item) => (
            <Pressable
              key={item.title}
              accessibilityRole="button"
              accessibilityLabel={item.title}
              onPress={() => router.push(screenHref(item.screenId) as never)}
              style={styles.queueCard}
            >
              <Text style={styles.queueTitle}>{item.title}</Text>
              <Text style={styles.queueText}>{item.detail}</Text>
            </Pressable>
          ))}
          <SolidButton
            label="Open forms"
            icon={FileText}
            onPress={() => router.push(screenHref("generated-forms") as never)}
          />
        </View>
      </View>

      <View style={styles.featureSections}>
        {directorFeatureSections.map((section) => (
          <DirectorFeatureSection key={section.title} section={section} />
        ))}
      </View>
    </View>
  );
}

function DirectorFeatureSection({
  section,
}: {
  section: (typeof directorFeatureSections)[number];
}) {
  const pages = section.screenIds
    .map((id) => getWeHonorPlanPage(id))
    .filter(Boolean) as WeHonorPlanPage[];

  return (
    <View style={styles.featureSection}>
      <View style={styles.featureSectionHeader}>
        <View style={styles.featureSectionCopy}>
          <Text style={styles.featureSectionTitle}>{section.title}</Text>
          <Text style={styles.featureSectionText}>{section.summary}</Text>
        </View>
      </View>
      <View style={styles.featureGrid}>
        {pages.map((page) => (
          <DirectorFeatureCard
            key={`${section.title}-${page.id}`}
            page={page}
          />
        ))}
      </View>
    </View>
  );
}

function DirectorFeatureCard({ page }: { page: WeHonorPlanPage }) {
  const router = useRouter();
  const Icon = iconForPage(page.id);

  return (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={page.title}
      onPress={() => router.push(screenHref(page.id) as never)}
      style={styles.featureCard}
    >
      <View style={styles.featureIcon}>
        <Icon color={semantic.action.primary} size={20} strokeWidth={1.85} />
      </View>
      <View style={styles.featureCopy}>
        <Text style={styles.featureTitle}>{page.title}</Text>
        <Text style={styles.featureText}>{page.purpose}</Text>
      </View>
      <ArrowRight color={semantic.text.secondary} size={16} strokeWidth={2} />
    </Pressable>
  );
}

function GeneratedFormsScreen({ isDesktop }: { isDesktop: boolean }) {
  return (
    <View style={styles.pageStack}>
      <View
        style={[styles.formsHero, isDesktop ? styles.formsHeroDesktop : null]}
      >
        <View style={styles.formsHeroCopy}>
          {isDesktop ? (
            <Badge label="Forms ready for review" tone="cedar" />
          ) : null}
          <Text
            style={[
              styles.displayTitle,
              isDesktop ? null : styles.displayTitleMobile,
            ]}
          >
            Generated forms are organized before signatures go out.
          </Text>
          <Text style={styles.displayBody}>
            Monarch staff can preview the document, confirm required fields, and
            send the right packet only after review.
          </Text>
          <SolidButton
            label="Generate packet"
            icon={FileSignature}
            onPress={() => undefined}
            fullWidth={!isDesktop}
          />
        </View>
        {isDesktop ? (
          <View style={styles.formsStatus}>
            <Text style={styles.formsStatusNumber}>5</Text>
            <Text style={styles.formsStatusLabel}>forms ready</Text>
          </View>
        ) : null}
      </View>

      <View style={[styles.dashboardGrid, isDesktop ? null : styles.stack]}>
        <View style={styles.panelSide}>
          <View style={styles.panelHeader}>
            <FileText color={semantic.action.primary} size={20} strokeWidth={1.9} />
            <Text style={styles.panelTitle}>Case packet</Text>
          </View>
          {generatedCasePacketForms.map((item) => (
            <View
              key={item.title}
              style={[
                styles.formQueueRow,
                item.active ? styles.formQueueRowActive : null,
              ]}
            >
              <FileSignature
                color={item.active ? semantic.action.primary : semantic.text.secondary}
                size={18}
                strokeWidth={1.8}
              />
              <Text style={styles.formQueueText}>{item.title}</Text>
            </View>
          ))}
        </View>

        <View style={styles.formPreviewStack}>
          <ReceiptOfRemainsPreview />
          <CremationAuthorizationPreview />
        </View>
      </View>
    </View>
  );
}

function FormTemplateScreen({
  page,
  isDesktop,
}: {
  page: WeHonorPlanPage;
  isDesktop: boolean;
}) {
  return (
    <View style={styles.pageStack}>
      <View
        style={[styles.formsHero, isDesktop ? styles.formsHeroDesktop : null]}
      >
        <View style={styles.formsHeroCopy}>
          {isDesktop ? (
            <Badge label="Template management" tone="cedar" />
          ) : null}
          <Text
            style={[
              styles.displayTitle,
              isDesktop ? null : styles.displayTitleMobile,
            ]}
          >
            {page.title}
          </Text>
          <Text style={styles.displayBody}>
            Edit field groups, required disclosures, initials, and signature
            blocks before the template is sent to families.
          </Text>
        </View>
        <SolidButton
          label="Save template"
          icon={CheckCircle2}
          onPress={() => undefined}
          fullWidth={!isDesktop}
        />
      </View>
      <View style={[styles.dashboardGrid, isDesktop ? null : styles.stack]}>
        <View style={styles.panelLarge}>
          <View style={styles.panelHeader}>
            <FileSignature color={semantic.action.primary} size={20} strokeWidth={1.9} />
            <Text style={styles.panelTitle}>
              Karen's current form templates
            </Text>
          </View>
          {templateForms.map((form) => (
            <View key={form.id} style={styles.templateSection}>
              <View>
                <Text style={styles.formSectionTitle}>{form.title}</Text>
                <Text style={styles.formSubline}>{form.versionLabel}</Text>
              </View>
              <View style={styles.templateGrid}>
                {form.requiredFields.map((field) => (
                  <View
                    key={`${form.id}-${field}`}
                    style={styles.templateField}
                  >
                    <Text style={styles.templateFieldLabel}>{field}</Text>
                    <View style={styles.templateFieldLine} />
                  </View>
                ))}
              </View>
            </View>
          ))}
        </View>
        <View style={styles.panelSide}>
          <Text style={styles.panelTitle}>Template controls</Text>
          {[
            "Every-family receipt",
            "Certificate count fields",
            "Cremated remains release",
            "Required initials",
            "Device disclosure",
            "Witness signature",
          ].map((item) => (
            <CheckRow key={item} label={item} />
          ))}
          <OutlineButton
            label="Preview form"
            icon={FileText}
            onPress={() => undefined}
          />
        </View>
      </View>
    </View>
  );
}

function OperationalScreen({
  page,
  isDesktop,
}: {
  page: WeHonorPlanPage;
  isDesktop: boolean;
}) {
  const Icon = iconForPage(page.id);
  return (
    <View style={styles.pageStack}>
      <View
        style={[
          styles.operationalHero,
          isDesktop ? styles.operationalHeroDesktop : null,
        ]}
      >
        <View style={styles.operationalCopy}>
          {isDesktop ? (
            <Badge
              label={groupConfig[page.group].label}
              tone={groupConfig[page.group].tone}
            />
          ) : null}
          <Text
            style={[
              styles.displayTitle,
              isDesktop ? null : styles.displayTitleMobile,
            ]}
          >
            {page.title}
          </Text>
          <Text style={styles.displayBody}>{page.purpose}</Text>
        </View>
        {isDesktop ? (
          <View style={styles.operationalBadge}>
            <Icon color={semantic.action.primary} size={28} strokeWidth={1.8} />
            <Text style={styles.operationalBadgeText}>Ready for staff use</Text>
          </View>
        ) : null}
      </View>

      <View style={[styles.dashboardGrid, isDesktop ? null : styles.stack]}>
        <View style={styles.panelLarge}>
          <View style={styles.panelHeader}>
            <Icon color={semantic.action.primary} size={20} strokeWidth={1.9} />
            <Text style={styles.panelTitle}>Workspace</Text>
          </View>
          <View style={styles.cardGrid}>
            {page.sees.slice(0, 6).map((item) => (
              <View key={item} style={styles.workCard}>
                <Text style={styles.workCardTitle}>{item}</Text>
                <Text style={styles.workCardText}>
                  Available in the current Monarch case view.
                </Text>
              </View>
            ))}
          </View>
        </View>

        <View style={styles.panelSide}>
          <Text style={styles.panelTitle}>Available actions</Text>
          {page.actions.slice(0, 5).map((action) => (
            <CheckRow key={action} label={action} />
          ))}
        </View>
      </View>
    </View>
  );
}

function ReceiptOfRemainsPreview() {
  return (
    <View style={styles.formPreview}>
      <View style={styles.formPreviewHeader}>
        <Image
          source={monarchBlueLogo}
          resizeMode="contain"
          style={styles.formLogo}
        />
        <View style={styles.formHeaderText}>
          <Text style={styles.formTitle}>{monarchReceiptOfRemains.title}</Text>
          <Text style={styles.formSubline}>
            {monarchReceiptOfRemains.versionLabel}
          </Text>
        </View>
      </View>

      <View style={styles.formFieldGrid}>
        {monarchReceiptOfRemains.requiredFields.slice(0, 6).map((field) => (
          <View key={field} style={styles.formField}>
            <Text style={styles.formFieldLabel}>{field}</Text>
            <View style={styles.formFieldLine} />
          </View>
        ))}
      </View>

      <View style={styles.formColumns}>
        <View style={styles.formColumn}>
          <Text style={styles.formSectionTitle}>Certificate receipt</Text>
          {monarchReceiptOfRemains.certificateCounts.map((label) => (
            <View key={label} style={styles.checkboxRow}>
              <View style={styles.checkbox} />
              <Text style={styles.checkboxText}>{label}</Text>
            </View>
          ))}
        </View>
        <View style={styles.formColumn}>
          <Text style={styles.formSectionTitle}>Staff use cases</Text>
          {monarchReceiptOfRemains.staffUseCases.map((item, index) => (
            <View key={item} style={styles.termRow}>
              <View style={styles.termNumber}>
                <Text style={styles.termNumberText}>{index + 1}</Text>
              </View>
              <Text style={styles.termText}>{item}</Text>
            </View>
          ))}
        </View>
      </View>

      <View style={styles.deviceNotice}>
        <ShieldCheck color={semantic.action.primary} size={18} strokeWidth={1.8} />
        <Text style={styles.deviceNoticeText}>
          {monarchReceiptOfRemains.releaseText}
        </Text>
      </View>

      <View style={styles.signatureGrid}>
        {monarchReceiptOfRemains.signatureBlocks.map((block, index) => (
          <View key={`${block.title}-${index}`} style={styles.signatureBlock}>
            <Text style={styles.signatureTitle}>{block.title}</Text>
            {block.fields.map((field) => (
              <View key={field} style={styles.signatureField}>
                <View style={styles.signatureLine} />
                <Text style={styles.signatureLabel}>{field}</Text>
              </View>
            ))}
          </View>
        ))}
      </View>

      <View style={styles.formFooter}>
        <Text style={styles.formFooterText}>
          {monarchReceiptOfRemains.footer.funeralHome}
        </Text>
        <Text style={styles.formFooterText}>
          {monarchReceiptOfRemains.footer.address}
        </Text>
        <Text style={styles.formFooterText}>
          {monarchReceiptOfRemains.footer.website}
        </Text>
      </View>
    </View>
  );
}

function CremationAuthorizationPreview() {
  return (
    <View style={styles.formPreview}>
      <View style={styles.formPreviewHeader}>
        <Image
          source={monarchBlueLogo}
          resizeMode="contain"
          style={styles.formLogo}
        />
        <View style={styles.formHeaderText}>
          <Text style={styles.formTitle}>
            {monarchCremationAuthorization.title}
          </Text>
          <Text style={styles.formSubline}>
            {monarchCremationAuthorization.versionLabel}
          </Text>
        </View>
      </View>

      <View style={styles.formFieldGrid}>
        {monarchCremationAuthorization.requiredFields
          .slice(0, 6)
          .map((field) => (
            <View key={field} style={styles.formField}>
              <Text style={styles.formFieldLabel}>{field}</Text>
              <View style={styles.formFieldLine} />
            </View>
          ))}
      </View>

      <View style={styles.formColumns}>
        <View style={styles.formColumn}>
          <Text style={styles.formSectionTitle}>Disposition choices</Text>
          {monarchCremationAuthorization.dispositionOptions.map((option) => (
            <View key={option} style={styles.checkboxRow}>
              <View style={styles.checkbox} />
              <Text style={styles.checkboxText}>{option}</Text>
            </View>
          ))}
        </View>
        <View style={styles.formColumn}>
          <Text style={styles.formSectionTitle}>Required disclosures</Text>
          {monarchCremationAuthorization.terms
            .slice(0, 4)
            .map((term, index) => (
              <View key={term} style={styles.termRow}>
                <View style={styles.termNumber}>
                  <Text style={styles.termNumberText}>{index + 1}</Text>
                </View>
                <Text style={styles.termText}>{term}</Text>
              </View>
            ))}
        </View>
      </View>

      <View style={styles.deviceNotice}>
        <ShieldCheck color={semantic.action.primary} size={18} strokeWidth={1.8} />
        <Text style={styles.deviceNoticeText}>
          {monarchCremationAuthorization.deviceDisclosure}
        </Text>
      </View>

      <View style={styles.signatureGrid}>
        {monarchCremationAuthorization.signatureBlocks.map((block) => (
          <View key={block.title} style={styles.signatureBlock}>
            <Text style={styles.signatureTitle}>{block.title}</Text>
            {block.fields.map((field) => (
              <View key={field} style={styles.signatureField}>
                <View style={styles.signatureLine} />
                <Text style={styles.signatureLabel}>{field}</Text>
              </View>
            ))}
          </View>
        ))}
      </View>
    </View>
  );
}

function Field({
  label,
  placeholder,
  icon: Icon,
  secureTextEntry,
}: {
  label: string;
  placeholder: string;
  icon: IconComponent;
  secureTextEntry?: boolean;
}) {
  return (
    <View style={styles.fieldWrap}>
      <Text style={styles.fieldLabel}>{label}</Text>
      <View style={styles.field}>
        <Icon color={semantic.text.secondary} size={18} strokeWidth={1.8} />
        <TextInput
          accessibilityLabel={label}
          placeholder={placeholder}
          placeholderTextColor={semantic.text.placeholder}
          secureTextEntry={secureTextEntry}
          style={styles.fieldInput}
        />
      </View>
    </View>
  );
}

function ChoiceCard({
  icon: Icon,
  title,
  body,
  onPress,
}: {
  icon: IconComponent;
  title: string;
  body: string;
  onPress: () => void;
}) {
  return (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={title}
      onPress={onPress}
      style={styles.choiceCard}
    >
      <View style={styles.choiceIcon}>
        <Icon color={semantic.action.primary} size={23} strokeWidth={1.9} />
      </View>
      <Text style={styles.choiceTitle}>{title}</Text>
      <Text style={styles.choiceBody}>{body}</Text>
      <View style={styles.choiceAction}>
        <Text style={styles.choiceActionText}>Continue</Text>
        <ArrowRight color={semantic.action.primary} size={15} strokeWidth={2} />
      </View>
    </Pressable>
  );
}

function ScreenCard({ page }: { page: WeHonorPlanPage }) {
  const router = useRouter();
  const Icon = iconForPage(page.id);
  return (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={page.title}
      onPress={() => router.push(screenHref(page.id) as never)}
      style={styles.screenCard}
    >
      <View style={styles.screenIcon}>
        <Icon color={semantic.action.primary} size={19} strokeWidth={1.8} />
      </View>
      <View style={styles.screenCopy}>
        <Text style={styles.screenTitle}>{page.title}</Text>
        <Text style={styles.screenBody}>{page.purpose}</Text>
      </View>
    </Pressable>
  );
}

function MetricCard({
  value,
  label,
  tone,
}: {
  value: string;
  label: string;
  tone: string;
}) {
  const toneStyle =
    tone === "gold"
      ? styles.metricGold
      : tone === "cedar"
        ? styles.metricCedar
        : styles.metricForest;
  return (
    <View style={[styles.metricCard, toneStyle]}>
      <Text style={styles.metricValue}>{value}</Text>
      <Text style={styles.metricLabel}>{label}</Text>
    </View>
  );
}

function SolidButton({
  label,
  icon: Icon,
  onPress,
  fullWidth = false,
}: {
  label: string;
  icon: IconComponent;
  onPress: () => void;
  fullWidth?: boolean;
}) {
  return (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={label}
      onPress={onPress}
      style={[styles.solidButton, fullWidth ? styles.fullWidth : null]}
    >
      <Text style={styles.solidButtonText}>{label}</Text>
      <Icon color={semantic.text.inverse} size={17} strokeWidth={2} />
    </Pressable>
  );
}

function OutlineButton({
  label,
  icon: Icon,
  onPress,
  fullWidth = false,
}: {
  label: string;
  icon: IconComponent;
  onPress: () => void;
  fullWidth?: boolean;
}) {
  return (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={label}
      onPress={onPress}
      style={[styles.outlineButton, fullWidth ? styles.fullWidth : null]}
    >
      <Icon color={semantic.action.primary} size={17} strokeWidth={2} />
      <Text style={styles.outlineButtonText}>{label}</Text>
    </Pressable>
  );
}

function Badge({
  label,
  tone = "forest",
}: {
  label: string;
  tone?: "forest" | "gold" | "cedar" | "mist";
}) {
  const toneStyle =
    tone === "gold"
      ? styles.badgeGold
      : tone === "cedar"
        ? styles.badgeCedar
        : tone === "mist"
          ? styles.badgeMist
          : styles.badgeForest;
  return (
    <View style={[styles.badge, toneStyle]}>
      <Text style={styles.badgeText}>{label}</Text>
    </View>
  );
}

function InfoRow({ icon: Icon, text }: { icon: IconComponent; text: string }) {
  return (
    <View style={styles.infoRow}>
      <Icon color={semantic.action.primary} size={17} strokeWidth={1.9} />
      <Text style={styles.infoText}>{text}</Text>
    </View>
  );
}

function CheckRow({ label }: { label: string }) {
  return (
    <View style={styles.checkRow}>
      <CheckCircle2 color={semantic.status.success.foreground} size={17} strokeWidth={2} />
      <Text style={styles.checkText}>{label}</Text>
    </View>
  );
}

function firstGroupHref(group: WeHonorPlanGroup) {
  const first = getWeHonorPagesByGroup(group)[0];
  return first ? screenHref(first.id) : demoStaffHref();
}

function screenHref(screenId: string) {
  return demoWorkspaceHref(screenId);
}

function iconForPage(pageId: string): IconComponent {
  if (pageId.includes("contract") || pageId.includes("financial"))
    return CreditCard;
  if (
    pageId.includes("document") ||
    pageId.includes("forms") ||
    pageId.includes("signature")
  )
    return FileText;
  if (pageId.includes("guest") || pageId.includes("memorial"))
    return MessageCircle;
  if (pageId.includes("service") || pageId.includes("event"))
    return CalendarDays;
  if (pageId.includes("case") || pageId.includes("dashboard"))
    return LayoutDashboard;
  if (pageId.includes("staff") || pageId.includes("permissions")) return Users;
  if (
    pageId.includes("catalog") ||
    pageId.includes("keepsake") ||
    pageId.includes("merchandise")
  )
    return PackageCheck;
  if (
    pageId.includes("settings") ||
    pageId.includes("branding") ||
    pageId.includes("template")
  )
    return Settings;
  if (pageId.includes("reports")) return BarChart3;
  if (pageId.includes("call")) return Phone;
  if (pageId.includes("checklist")) return ClipboardCheck;
  if (pageId.includes("library")) return LibraryBig;
  if (pageId.includes("upload")) return FolderOpen;
  if (pageId.includes("home")) return Home;
  if (pageId.includes("folder")) return QrCode;
  return FileText;
}

const styles = StyleSheet.create({
  publicSafeArea: {
    flex: 1,
    backgroundColor: semantic.surface.canvas,
  },
  publicContent: {
    width: "100%",
    maxWidth: 1120,
    alignSelf: "center",
    paddingHorizontal: 22,
    paddingTop: 14,
    paddingBottom: 34,
    gap: spacing.lg,
  },
  publicHeader: {
    minHeight: 92,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    gap: spacing.md,
  },
  publicHeaderMobile: {
    minHeight: 82,
    alignItems: "flex-start",
    flexDirection: "column",
  },
  publicLogoButton: {
    minHeight: 70,
    justifyContent: "center",
  },
  publicLogo: {
    width: 250,
    height: 108,
  },
  publicLogoMobile: {
    width: 230,
    height: 96,
  },
  publicHeaderActions: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 10,
  },
  safeArea: {
    flex: 1,
    backgroundColor: semantic.action.primaryPressed,
  },
  directorLoginContent: {
    flexGrow: 1,
    width: "100%",
    maxWidth: 1180,
    alignSelf: "center",
    justifyContent: "center",
    padding: spacing.lg,
    backgroundColor: semantic.surface.canvas,
  },
  shell: {
    flex: 1,
    flexDirection: "row",
    backgroundColor: semantic.surface.canvas,
  },
  shellMobile: {
    flexDirection: "column",
  },
  mobileAccountBar: {
    minHeight: 60,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingHorizontal: 16,
    backgroundColor: semantic.surface.staffRaised,
    borderBottomWidth: 1,
    borderBottomColor: semantic.border.staff,
  },
  mobileOrgButton: {
    minHeight: 44,
    flexDirection: "row",
    alignItems: "center",
    gap: 9,
  },
  mobileLogoMarkFrame: {
    width: 32,
    height: 32,
    borderRadius: 8,
    overflow: "hidden",
    backgroundColor: semantic.surface.staff,
    borderWidth: 1,
    borderColor: semantic.overlay.ink,
  },
  mobileLogoMarkText: {
    color: semantic.action.primary,
    fontFamily: "CormorantGaramond_700Bold",
    fontSize: 21,
    lineHeight: 30,
    textAlign: "center",
  },
  mobileOrgName: {
    color: semantic.text.primary,
    fontFamily: "Manrope_800ExtraBold",
    fontSize: 16,
    lineHeight: 22,
  },
  mobileAccountButton: {
    minWidth: 44,
    minHeight: 44,
    alignItems: "center",
    justifyContent: "center",
  },
  mobileAccountText: {
    width: 30,
    height: 30,
    borderRadius: 15,
    overflow: "hidden",
    textAlign: "center",
    textAlignVertical: "center",
    color: semantic.text.inverse,
    backgroundColor: semantic.action.primary,
    fontFamily: "Manrope_800ExtraBold",
    fontSize: 11,
    lineHeight: 30,
  },
  nav: {
    width: 112,
    backgroundColor: semantic.action.primary,
    paddingVertical: 18,
    paddingHorizontal: 12,
    gap: 16,
    alignItems: "center",
  },
  navMobile: {
    position: "absolute",
    left: 0,
    right: 0,
    bottom: 0,
    width: "100%",
    minHeight: 76,
    flexDirection: "row",
    paddingTop: 8,
    paddingBottom: 10,
    paddingHorizontal: 10,
    justifyContent: "space-between",
    borderTopWidth: 1,
    borderTopColor: semantic.border.inverse,
  },
  navLogo: {
    width: 82,
    minHeight: 72,
    borderRadius: 22,
    backgroundColor: semantic.overlay.inverseSubtle,
    alignItems: "center",
    justifyContent: "center",
    overflow: "hidden",
  },
  navLogoMobile: {
    width: 68,
    minHeight: 52,
    borderRadius: 18,
  },
  navLogoCompact: {
    display: "none",
  },
  navLogoImage: {
    width: 118,
    height: 62,
  },
  navLogoImageMobile: {
    width: 76,
    height: 42,
  },
  navItems: {
    width: "100%",
    gap: 10,
  },
  navItemsMobile: {
    flex: 1,
    flexDirection: "row",
    justifyContent: "space-around",
    alignItems: "center",
    gap: 4,
  },
  navItem: {
    minHeight: 62,
    borderRadius: 18,
    alignItems: "center",
    justifyContent: "center",
    gap: 5,
  },
  navItemMobile: {
    flex: 1,
    minHeight: 58,
    borderRadius: 12,
  },
  navItemActive: {
    backgroundColor: semantic.overlay.dark,
  },
  navItemText: {
    color: semantic.text.inverseMuted,
    fontFamily: "Manrope_800ExtraBold",
    fontSize: 10,
    lineHeight: 13,
  },
  navItemTextMobile: {
    color: semantic.text.inverseMuted,
    fontSize: 8,
    lineHeight: 10,
    textAlign: "center",
  },
  navItemTextActive: {
    color: semantic.accent.soft,
  },
  scroll: {
    flex: 1,
  },
  content: {
    padding: spacing.lg,
    gap: spacing.lg,
  },
  contentMobile: {
    padding: 14,
    paddingTop: 24,
    paddingBottom: 104,
  },
  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    gap: spacing.md,
    paddingBottom: spacing.sm,
    borderBottomWidth: 1,
    borderBottomColor: semantic.overlay.line,
  },
  headerMobile: {
    alignItems: "flex-start",
    flexDirection: "column",
  },
  headerBrand: {
    gap: 4,
  },
  headerLogo: {
    width: 230,
    height: 104,
  },
  headerLogoMobile: {
    width: 210,
    height: 88,
  },
  headerSubline: {
    color: semantic.action.primary,
    fontFamily: "Manrope_800ExtraBold",
    fontSize: 11,
    lineHeight: 15,
    textTransform: "uppercase",
  },
  headerActions: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 10,
  },
  pageStack: {
    gap: spacing.lg,
  },
  familyHero: {
    gap: spacing.md,
  },
  familyHeroDesktop: {
    flexDirection: "row",
    alignItems: "stretch",
  },
  familyHeroCopy: {
    flex: 1.45,
    gap: 14,
    minWidth: 0,
  },
  familyHeroCopyMobile: {
    paddingBottom: 18,
  },
  displayTitle: {
    color: semantic.text.primary,
    fontFamily: "CormorantGaramond_700Bold",
    fontSize: 48,
    lineHeight: 50,
  },
  displayTitleMobile: {
    fontSize: 32,
    lineHeight: 36,
  },
  displayBody: {
    color: semantic.text.secondary,
    fontFamily: "Manrope_500Medium",
    fontSize: 16,
    lineHeight: 25,
    maxWidth: 740,
  },
  actionRow: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 10,
  },
  actionRowMobile: {
    width: "100%",
  },
  contactCard: {
    flex: 1,
    minWidth: 0,
    maxWidth: "100%",
    gap: 12,
    padding: 18,
    borderRadius: 22,
    backgroundColor: semantic.surface.raised,
    borderWidth: 1,
    borderColor: semantic.overlay.line,
    ...shadows.soft,
  },
  contactCardTitle: {
    color: semantic.text.primary,
    fontFamily: "CormorantGaramond_700Bold",
    fontSize: 30,
    lineHeight: 32,
  },
  optionGrid: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: spacing.md,
  },
  choiceCard: {
    flex: 1,
    minWidth: 0,
    maxWidth: "100%",
    gap: 11,
    padding: 18,
    borderRadius: 22,
    backgroundColor: semantic.surface.raised,
    borderWidth: 1,
    borderColor: semantic.overlay.line,
  },
  choiceIcon: {
    width: 48,
    height: 48,
    borderRadius: 24,
    backgroundColor: semantic.overlay.forestSubtle,
    alignItems: "center",
    justifyContent: "center",
  },
  choiceTitle: {
    color: semantic.text.primary,
    fontFamily: "Manrope_800ExtraBold",
    fontSize: 18,
    lineHeight: 24,
  },
  choiceBody: {
    color: semantic.text.secondary,
    fontFamily: "Manrope_500Medium",
    fontSize: 14,
    lineHeight: 21,
  },
  choiceAction: {
    flexDirection: "row",
    alignItems: "center",
    gap: 5,
  },
  choiceActionText: {
    color: semantic.action.primary,
    fontFamily: "Manrope_800ExtraBold",
    fontSize: 13,
  },
  workBand: {
    gap: spacing.md,
    padding: spacing.lg,
    borderRadius: 24,
    backgroundColor: semantic.surface.subtle,
  },
  workBandDesktop: {
    flexDirection: "row",
    alignItems: "center",
  },
  bandCopy: {
    flex: 1,
    gap: 8,
  },
  sectionEyebrow: {
    color: semantic.accent.strong,
    fontFamily: "Manrope_800ExtraBold",
    fontSize: 12,
    lineHeight: 16,
    textTransform: "uppercase",
  },
  sectionTitle: {
    color: semantic.text.primary,
    fontFamily: "CormorantGaramond_700Bold",
    fontSize: 36,
    lineHeight: 38,
  },
  sectionBody: {
    color: semantic.text.secondary,
    fontFamily: "Manrope_500Medium",
    fontSize: 14,
    lineHeight: 22,
  },
  checkList: {
    flex: 1,
    minWidth: 0,
    maxWidth: "100%",
    gap: 10,
  },
  loginWrap: {
    gap: spacing.lg,
  },
  loginWrapDesktop: {
    flexDirection: "row",
    alignItems: "center",
    minHeight: 560,
  },
  loginCopy: {
    flex: 1,
    gap: 14,
  },
  securityList: {
    gap: 10,
    paddingTop: 6,
  },
  loginPanel: {
    flex: 0.82,
    minWidth: 0,
    maxWidth: "100%",
    gap: 13,
    padding: 22,
    borderRadius: 24,
    backgroundColor: semantic.surface.raised,
    borderWidth: 1,
    borderColor: semantic.overlay.line,
    ...shadows.soft,
  },
  loginIcon: {
    width: 52,
    height: 52,
    borderRadius: 26,
    backgroundColor: semantic.action.primary,
    alignItems: "center",
    justifyContent: "center",
  },
  loginTitle: {
    color: semantic.text.primary,
    fontFamily: "Manrope_800ExtraBold",
    fontSize: 22,
    lineHeight: 27,
  },
  loginText: {
    color: semantic.text.secondary,
    fontFamily: "Manrope_500Medium",
    fontSize: 14,
    lineHeight: 21,
  },
  dashboardHero: {
    gap: spacing.md,
  },
  dashboardHeroDesktop: {
    flexDirection: "row",
    alignItems: "flex-end",
    justifyContent: "space-between",
  },
  dashboardHeroCopy: {
    flex: 1,
    gap: 14,
  },
  searchBox: {
    minHeight: 52,
    minWidth: 0,
    maxWidth: "100%",
    flexDirection: "row",
    alignItems: "center",
    gap: 10,
    borderRadius: 26,
    paddingHorizontal: 18,
    backgroundColor: semantic.surface.raised,
    borderWidth: 1,
    borderColor: semantic.overlay.line,
  },
  searchText: {
    color: semantic.text.secondary,
    fontFamily: "Manrope_600SemiBold",
    fontSize: 13,
  },
  metricGrid: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: spacing.md,
  },
  metricCard: {
    flex: 1,
    minWidth: 160,
    padding: 18,
    borderRadius: 22,
    borderWidth: 1,
  },
  metricForest: {
    backgroundColor: semantic.overlay.forestSubtle,
    borderColor: semantic.overlay.forestStrong,
  },
  metricGold: {
    backgroundColor: semantic.overlay.goldSubtle,
    borderColor: semantic.overlay.gold,
  },
  metricCedar: {
    backgroundColor: semantic.overlay.cedarSubtle,
    borderColor: semantic.overlay.cedarSubtle,
  },
  metricValue: {
    color: semantic.text.primary,
    fontFamily: "CormorantGaramond_700Bold",
    fontSize: 40,
    lineHeight: 41,
  },
  metricLabel: {
    color: semantic.text.secondary,
    fontFamily: "Manrope_800ExtraBold",
    fontSize: 12,
    lineHeight: 17,
  },
  dashboardGrid: {
    flexDirection: "row",
    gap: spacing.md,
    alignItems: "stretch",
  },
  stack: {
    flexDirection: "column",
  },
  panelLarge: {
    flex: 1.7,
    minWidth: 0,
    maxWidth: "100%",
    gap: spacing.md,
    padding: 18,
    borderRadius: 24,
    backgroundColor: semantic.surface.raised,
    borderWidth: 1,
    borderColor: semantic.overlay.line,
  },
  panelSide: {
    flex: 1,
    minWidth: 0,
    maxWidth: "100%",
    gap: 13,
    padding: 18,
    borderRadius: 24,
    backgroundColor: semantic.surface.raised,
    borderWidth: 1,
    borderColor: semantic.overlay.line,
  },
  panelHeader: {
    flexDirection: "row",
    flexWrap: "wrap",
    alignItems: "center",
    gap: 9,
  },
  panelTitle: {
    flexShrink: 1,
    color: semantic.text.primary,
    fontFamily: "Manrope_800ExtraBold",
    fontSize: 16,
    lineHeight: 22,
  },
  caseTable: {
    gap: 8,
  },
  caseRow: {
    minHeight: 72,
    flexDirection: "row",
    flexWrap: "wrap",
    alignItems: "center",
    gap: 12,
    padding: 14,
    borderRadius: 18,
    backgroundColor: semantic.surface.canvas,
  },
  caseFamily: {
    flex: 1.4,
    minWidth: 150,
  },
  caseName: {
    color: semantic.text.primary,
    fontFamily: "Manrope_800ExtraBold",
    fontSize: 14,
    lineHeight: 19,
  },
  caseMeta: {
    color: semantic.text.secondary,
    fontFamily: "Manrope_500Medium",
    fontSize: 12,
    lineHeight: 17,
  },
  caseOwner: {
    color: semantic.action.primary,
    fontFamily: "Manrope_800ExtraBold",
    fontSize: 12,
    lineHeight: 17,
    minWidth: 76,
  },
  caseNext: {
    flex: 1,
    minWidth: 140,
    color: semantic.text.secondary,
    fontFamily: "Manrope_600SemiBold",
    fontSize: 12,
    lineHeight: 17,
  },
  queueCard: {
    gap: 5,
    padding: 13,
    borderRadius: 16,
    backgroundColor: semantic.surface.canvas,
    borderWidth: 1,
    borderColor: semantic.overlay.line,
  },
  queueTitle: {
    color: semantic.text.primary,
    fontFamily: "Manrope_800ExtraBold",
    fontSize: 13,
    lineHeight: 18,
  },
  queueText: {
    color: semantic.text.secondary,
    fontFamily: "Manrope_500Medium",
    fontSize: 12,
    lineHeight: 18,
  },
  featureSections: {
    gap: spacing.lg,
  },
  featureSection: {
    gap: spacing.md,
  },
  featureSectionHeader: {
    flexDirection: "row",
    alignItems: "flex-end",
    justifyContent: "space-between",
    gap: spacing.md,
  },
  featureSectionCopy: {
    flex: 1,
    gap: 5,
  },
  featureSectionTitle: {
    color: semantic.text.primary,
    fontFamily: "CormorantGaramond_700Bold",
    fontSize: 34,
    lineHeight: 36,
  },
  featureSectionText: {
    color: semantic.text.secondary,
    fontFamily: "Manrope_500Medium",
    fontSize: 14,
    lineHeight: 22,
    maxWidth: 820,
  },
  featureGrid: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: spacing.md,
  },
  featureCard: {
    flex: 1,
    minWidth: 0,
    maxWidth: "100%",
    minHeight: 124,
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 12,
    padding: 16,
    borderRadius: 20,
    backgroundColor: semantic.surface.raised,
    borderWidth: 1,
    borderColor: semantic.overlay.line,
  },
  featureIcon: {
    width: 42,
    height: 42,
    borderRadius: 21,
    backgroundColor: semantic.overlay.forestSubtle,
    alignItems: "center",
    justifyContent: "center",
  },
  featureCopy: {
    flex: 1,
    minWidth: 0,
    gap: 5,
  },
  featureTitle: {
    color: semantic.text.primary,
    fontFamily: "Manrope_800ExtraBold",
    fontSize: 14,
    lineHeight: 19,
  },
  featureText: {
    color: semantic.text.secondary,
    fontFamily: "Manrope_500Medium",
    fontSize: 12,
    lineHeight: 18,
  },
  screenGrid: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: spacing.md,
  },
  screenCard: {
    flex: 1,
    minWidth: 0,
    maxWidth: "100%",
    flexDirection: "row",
    gap: 12,
    padding: 15,
    borderRadius: 20,
    backgroundColor: semantic.surface.raised,
    borderWidth: 1,
    borderColor: semantic.overlay.line,
  },
  screenIcon: {
    width: 42,
    height: 42,
    borderRadius: 21,
    backgroundColor: semantic.overlay.forestSubtle,
    alignItems: "center",
    justifyContent: "center",
  },
  screenCopy: {
    flex: 1,
    minWidth: 0,
    gap: 4,
  },
  screenTitle: {
    color: semantic.text.primary,
    fontFamily: "Manrope_800ExtraBold",
    fontSize: 13,
    lineHeight: 18,
  },
  screenBody: {
    color: semantic.text.secondary,
    fontFamily: "Manrope_500Medium",
    fontSize: 12,
    lineHeight: 18,
  },
  formsHero: {
    gap: spacing.md,
  },
  formsHeroDesktop: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },
  formsHeroCopy: {
    flex: 1,
    gap: 14,
  },
  formsStatus: {
    width: 150,
    minHeight: 126,
    borderRadius: 24,
    backgroundColor: semantic.action.primary,
    alignItems: "center",
    justifyContent: "center",
  },
  formsStatusNumber: {
    color: semantic.text.inverse,
    fontFamily: "CormorantGaramond_700Bold",
    fontSize: 52,
    lineHeight: 52,
  },
  formsStatusLabel: {
    color: semantic.text.inverseMuted,
    fontFamily: "Manrope_800ExtraBold",
    fontSize: 12,
    lineHeight: 16,
  },
  formQueueRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 10,
    padding: 13,
    borderRadius: 16,
    backgroundColor: semantic.surface.canvas,
  },
  formQueueRowActive: {
    backgroundColor: semantic.overlay.forestSubtle,
  },
  formQueueText: {
    flex: 1,
    color: semantic.text.primary,
    fontFamily: "Manrope_700Bold",
    fontSize: 13,
    lineHeight: 19,
  },
  formPreviewStack: {
    flex: 1.8,
    minWidth: 0,
    maxWidth: "100%",
    gap: spacing.md,
  },
  formPreview: {
    flex: 1,
    minWidth: 0,
    maxWidth: "100%",
    gap: spacing.md,
    padding: 18,
    borderRadius: 24,
    backgroundColor: semantic.surface.raised,
    borderWidth: 1,
    borderColor: semantic.overlay.line,
  },
  formPreviewHeader: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
    flexWrap: "wrap",
  },
  formLogo: {
    width: 180,
    height: 86,
  },
  formHeaderText: {
    flex: 1,
    minWidth: 0,
  },
  formTitle: {
    color: semantic.text.primary,
    fontFamily: "Manrope_800ExtraBold",
    fontSize: 19,
    lineHeight: 25,
  },
  formSubline: {
    color: semantic.text.secondary,
    fontFamily: "Manrope_600SemiBold",
    fontSize: 12,
    lineHeight: 18,
  },
  formFieldGrid: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 10,
  },
  formField: {
    flex: 1,
    minWidth: 0,
    maxWidth: "100%",
    gap: 7,
    padding: 12,
    borderRadius: 14,
    backgroundColor: semantic.surface.canvas,
  },
  formFieldLabel: {
    color: semantic.text.primary,
    fontFamily: "Manrope_700Bold",
    fontSize: 12,
    lineHeight: 17,
  },
  formFieldLine: {
    height: 1,
    backgroundColor: semantic.overlay.forestStrong,
  },
  formColumns: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: spacing.md,
  },
  formColumn: {
    flex: 1,
    minWidth: 0,
    maxWidth: "100%",
    gap: 10,
  },
  formSectionTitle: {
    color: semantic.text.primary,
    fontFamily: "Manrope_800ExtraBold",
    fontSize: 14,
    lineHeight: 20,
  },
  checkboxRow: {
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 9,
  },
  checkbox: {
    width: 18,
    height: 18,
    borderRadius: 5,
    borderWidth: 1,
    borderColor: semantic.action.primary,
    marginTop: 1,
  },
  checkboxText: {
    flex: 1,
    color: semantic.text.secondary,
    fontFamily: "Manrope_500Medium",
    fontSize: 12,
    lineHeight: 18,
  },
  termRow: {
    flexDirection: "row",
    gap: 8,
    alignItems: "flex-start",
  },
  termNumber: {
    width: 22,
    height: 22,
    borderRadius: 11,
    backgroundColor: semantic.overlay.forestSubtle,
    alignItems: "center",
    justifyContent: "center",
  },
  termNumberText: {
    color: semantic.action.primary,
    fontFamily: "Manrope_800ExtraBold",
    fontSize: 10,
  },
  termText: {
    flex: 1,
    color: semantic.text.secondary,
    fontFamily: "Manrope_500Medium",
    fontSize: 11,
    lineHeight: 17,
  },
  deviceNotice: {
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 10,
    padding: 12,
    borderRadius: 16,
    backgroundColor: semantic.overlay.forestSubtle,
  },
  deviceNoticeText: {
    flex: 1,
    color: semantic.text.primary,
    fontFamily: "Manrope_700Bold",
    fontSize: 12,
    lineHeight: 18,
  },
  signatureGrid: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: spacing.md,
  },
  signatureBlock: {
    flex: 1,
    minWidth: 0,
    maxWidth: "100%",
    gap: 10,
    padding: 14,
    borderRadius: 16,
    borderWidth: 1,
    borderColor: semantic.overlay.line,
  },
  signatureTitle: {
    color: semantic.text.primary,
    fontFamily: "Manrope_800ExtraBold",
    fontSize: 13,
    lineHeight: 18,
  },
  signatureField: {
    gap: 4,
  },
  signatureLine: {
    height: 1,
    backgroundColor: semantic.overlay.forestStrong,
  },
  signatureLabel: {
    color: semantic.text.secondary,
    fontFamily: "Manrope_600SemiBold",
    fontSize: 10,
    lineHeight: 14,
  },
  formFooter: {
    alignItems: "center",
    gap: 3,
    paddingTop: 8,
    borderTopWidth: 1,
    borderTopColor: semantic.overlay.forest,
  },
  formFooterText: {
    color: semantic.text.secondary,
    fontFamily: "Manrope_600SemiBold",
    fontSize: 10,
    lineHeight: 14,
    textAlign: "center",
  },
  templateSection: {
    gap: 12,
    paddingVertical: 8,
  },
  templateGrid: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 10,
  },
  templateField: {
    flex: 1,
    minWidth: 0,
    maxWidth: "100%",
    gap: 8,
    padding: 12,
    borderRadius: 15,
    backgroundColor: semantic.surface.canvas,
  },
  templateFieldLabel: {
    color: semantic.text.primary,
    fontFamily: "Manrope_700Bold",
    fontSize: 12,
    lineHeight: 17,
  },
  templateFieldLine: {
    height: 1,
    backgroundColor: semantic.overlay.forestStrong,
  },
  operationalHero: {
    gap: spacing.md,
  },
  operationalHeroDesktop: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },
  operationalCopy: {
    flex: 1,
    gap: 14,
  },
  operationalBadge: {
    width: 190,
    minHeight: 136,
    gap: 10,
    borderRadius: 24,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: semantic.surface.raised,
    borderWidth: 1,
    borderColor: semantic.overlay.line,
  },
  operationalBadgeText: {
    color: semantic.action.primary,
    fontFamily: "Manrope_800ExtraBold",
    fontSize: 13,
    lineHeight: 18,
  },
  cardGrid: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 10,
  },
  workCard: {
    flex: 1,
    minWidth: 0,
    maxWidth: "100%",
    padding: 14,
    borderRadius: 17,
    backgroundColor: semantic.surface.canvas,
    gap: 6,
  },
  workCardTitle: {
    color: semantic.text.primary,
    fontFamily: "Manrope_800ExtraBold",
    fontSize: 13,
    lineHeight: 18,
  },
  workCardText: {
    color: semantic.text.secondary,
    fontFamily: "Manrope_500Medium",
    fontSize: 12,
    lineHeight: 18,
  },
  fieldWrap: {
    gap: 6,
  },
  fieldLabel: {
    color: semantic.text.primary,
    fontFamily: "Manrope_800ExtraBold",
    fontSize: 12,
    lineHeight: 16,
  },
  field: {
    minHeight: 50,
    flexDirection: "row",
    alignItems: "center",
    gap: 9,
    paddingHorizontal: 13,
    borderRadius: 15,
    backgroundColor: semantic.surface.canvas,
    borderWidth: 1,
    borderColor: semantic.overlay.line,
  },
  fieldInput: {
    flex: 1,
    color: semantic.text.primary,
    fontFamily: "Manrope_600SemiBold",
    fontSize: 14,
    minHeight: 46,
  },
  solidButton: {
    minHeight: 46,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    borderRadius: 23,
    paddingHorizontal: 17,
    backgroundColor: semantic.action.primary,
  },
  solidButtonText: {
    color: semantic.text.inverse,
    fontFamily: "Manrope_800ExtraBold",
    fontSize: 13,
    lineHeight: 18,
  },
  outlineButton: {
    minHeight: 46,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    borderRadius: 23,
    paddingHorizontal: 17,
    backgroundColor: semantic.surface.raised,
    borderWidth: 1,
    borderColor: semantic.overlay.forestStrong,
  },
  outlineButtonText: {
    color: semantic.action.primary,
    fontFamily: "Manrope_800ExtraBold",
    fontSize: 13,
    lineHeight: 18,
  },
  fullWidth: {
    width: "100%",
  },
  badge: {
    alignSelf: "flex-start",
    minHeight: 32,
    borderRadius: 16,
    paddingHorizontal: 12,
    alignItems: "center",
    justifyContent: "center",
  },
  badgeForest: {
    backgroundColor: semantic.overlay.forestSubtle,
  },
  badgeGold: {
    backgroundColor: semantic.overlay.goldSubtle,
  },
  badgeCedar: {
    backgroundColor: semantic.overlay.cedarSubtle,
  },
  badgeMist: {
    backgroundColor: semantic.surface.muted,
  },
  badgeText: {
    color: semantic.action.primary,
    fontFamily: "Manrope_800ExtraBold",
    fontSize: 11,
    lineHeight: 15,
    textTransform: "uppercase",
  },
  infoRow: {
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 9,
  },
  infoText: {
    flex: 1,
    color: semantic.text.secondary,
    fontFamily: "Manrope_600SemiBold",
    fontSize: 13,
    lineHeight: 19,
  },
  checkRow: {
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 9,
  },
  checkText: {
    flex: 1,
    color: semantic.text.secondary,
    fontFamily: "Manrope_600SemiBold",
    fontSize: 13,
    lineHeight: 19,
  },
});
